import React from 'react';
import type { GetServerSidePropsResult, NextPage } from 'next';
import { SWRConfig } from 'swr';

import { doFetch } from 'common/baseFetch';
import TideCharts from 'containers/TideCharts';
import { UserTypeContextProvider } from 'contexts/UserTypeContext';
import { getUser } from 'common/api/user';
import type { WavetrakServerSidePropsContext } from 'pages';
import type { AppPageProps } from 'pages/_app';
import type { SpotReport } from 'types/spot';
import type { GeoLocation } from 'types/locationView';
import type { SubregionOverview } from 'types/subregion';
import TideChartsPageMeta from 'components/TideChartsPageMeta/TideChartsPageMeta';
import type { TideResponse } from 'types/tide';
import {
  fetchRegionalOverviewPath,
  spotReportFetchPath,
  sunlightFetchPath,
  tidesFetchPath,
} from 'utils/urls/tideCharts';
import { getOrCreateStore } from 'stores';

interface TidesPageProps extends AppPageProps {
  defaultData: { [x: string]: unknown };
  entitlements?: string[] | null;
  referrer: string;
  spotGeo: GeoLocation;
  spotId: string;
  spotName: string;
  spotSlug: string;
  subregionId: string;
  subregionName: string;
  timezone: string;
}

const TidesPage: NextPage<TidesPageProps> = ({
  defaultData,
  entitlements,
  referrer,
  spotGeo,
  spotId,
  spotName,
  spotSlug,
  subregionId,
  subregionName,
  timezone,
}) => (
  <>
    <TideChartsPageMeta spotName={spotName} spotId={spotId} spotSlug={spotSlug} />
    <SWRConfig value={{ fallback: defaultData }}>
      <UserTypeContextProvider entitlements={entitlements || undefined} isAnonUser={!entitlements}>
        <TideCharts
          spotId={spotId}
          spotName={spotName}
          spotGeo={spotGeo}
          subregionId={subregionId}
          spotSlug={spotSlug}
          subregionName={subregionName}
          timezone={timezone}
          referrer={referrer}
        />
      </UserTypeContextProvider>
    </SWRConfig>
  </>
);

export const getServerSideProps = async (
  context: WavetrakServerSidePropsContext,
): Promise<GetServerSidePropsResult<TidesPageProps>> => {
  const { query, req } = context;
  const { cookies } = req;
  const { spotId, spotSlug } = query as { spotSlug: string; spotId: string };
  const store = getOrCreateStore();
  const state = store.getState();
  const isServer = true;
  const user = await getUser(
    {
      ...context,
      pathname: context.resolvedUrl,
    },
    isServer,
  );

  const tidesFetchUrl = tidesFetchPath(spotId);
  const sunlightFetchUrl = sunlightFetchPath(spotId);
  const spotReportFetchUrl = spotReportFetchPath(spotId);

  const [spotReport, defaultTidesForSpot, defaultSunlightForSpot] = await Promise.all([
    doFetch(spotReportFetchUrl, { cookies }) as Promise<SpotReport>,
    doFetch(tidesFetchUrl, { cookies }) as Promise<TideResponse>,
    doFetch(sunlightFetchUrl, { cookies }),
  ]);

  const subregionalFetchUrl = fetchRegionalOverviewPath(spotReport.spot.subregion._id);
  const [subregionOverview] = await Promise.all([
    doFetch(subregionalFetchUrl, { cookies }) as Promise<SubregionOverview>,
  ]);

  return {
    props: {
      defaultData: {
        [sunlightFetchUrl]: defaultSunlightForSpot,
        [tidesFetchUrl]: defaultTidesForSpot,
      },
      enableCssBaseline: true,
      enableTheme: true,
      entitlements: user?.details ? user?.entitlements : null,
      isAppBannerEnabled: true,
      referrer: context.req.headers.referer || '',
      spotGeo: { lat: spotReport.spot.lat, lon: spotReport.spot.lon },
      spotId,
      spotName: spotReport.spot.name,
      spotSlug,
      ssrReduxState: state,
      subregionId: spotReport.spot.subregion._id,
      subregionName: subregionOverview.data.name,
      timezone: subregionOverview.associated.timezone,
    },
  };
};

export default TidesPage;
